import { flow, isEqual, isNull, isUndefined, set, uniq, unset } from 'lodash/fp';
import { toast } from 'react-toastify';
import { all, call, debounce, fork, put, select, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';

import { deleteSubCounterpartyTypes, fetchAllAvailableOpinionSubCounterpartyTypes, updateRelevantOpinionSummaries, upsertOpinionSubCounterpartyType } from '../../../../services/opinion';
import { deleteOpinionSubCounterpartyTypesFailed, deleteOpinionSubCounterpartyTypesSuccessful, fetchOpinionSubCounterpartyTypesFailed, fetchOpinionSubCounterpartyTypesStarted, fetchOpinionSubCounterpartyTypesSuccessful, setJurisdictionSubCounterpartyTypes, setSavedParentCounterparties, setSelectedOpinionSubCounterparty, updateOpinionSubCounterparty, upsertOpinionSubCounterpartyTypesFailed, upsertOpinionSubCounterpartyTypesSuccessful } from './actions';
import { getAvailableSubCounterpartyTypes, getMultiParentCounterpartyTypes, getOpinionScopesToUpdate, getSavedParentCounterpartyTypes, getSavedSelectedSubCounterpartyType, getSelectedSubCounterpartyType, getSubCounterpartiesToDelete } from './selectors';
import { SubCounterpartyActionTypes, SubCounterpartyType } from './types';
import { OpinionScope } from '../../opinions/store/types';

export function* attemptFetchAvailableOpinionSubCounterpartyTypes({ payload }: ReturnType<typeof fetchOpinionSubCounterpartyTypesStarted>) {
    const { opinionJurisdiction, opinionScope, parentCounterpartyTypeFilter, establishIfIncluded } = payload;
    try {
        const subCounterpartyTypes: SubCounterpartyType[] = yield call(fetchAllAvailableOpinionSubCounterpartyTypes, {
            opinionJurisdiction,
            opinionScope,
            parentCounterpartyTypeFilter,
            establishIfIncluded
        });
        if (isUndefined(parentCounterpartyTypeFilter)) {
            yield put(setJurisdictionSubCounterpartyTypes(subCounterpartyTypes));
        }
        yield put(fetchOpinionSubCounterpartyTypesSuccessful(subCounterpartyTypes));
    } catch (e) {
        yield put(fetchOpinionSubCounterpartyTypesFailed((e as Error).message));
    }
}

function* attemptFetchAvailableOpinionSubCounterpartyTypesWatcher() {
    yield takeLatest(SubCounterpartyActionTypes.FETCH_OPINION_SUB_COUNTERPARTY_TYPES_STARTED, attemptFetchAvailableOpinionSubCounterpartyTypes);
}

export function* attemptUpsertOpinionSubCounterpartyTypes() {
    try {
        const subCounterpartyType: SubCounterpartyType = yield select(getSelectedSubCounterpartyType);
        const { isSystem, jurisdiction } = subCounterpartyType;
        const multiParentCounterparties: string[] = yield select(getMultiParentCounterpartyTypes);
        const savedParentCounterparties: string[] = yield select(getSavedParentCounterpartyTypes);
        if (isUndefined(subCounterpartyType.subCounterpartyTypeId)) {
            for (const parentCounterpartyType of multiParentCounterparties) {
                yield call(upsertOpinionSubCounterpartyType, { ...subCounterpartyType, parentCounterpartyType });
            }
        } else {
            const savedSubCounterpartyType: SubCounterpartyType = yield select(getSavedSelectedSubCounterpartyType);
            if (!isEqual(savedSubCounterpartyType, subCounterpartyType)) {
                yield call(upsertOpinionSubCounterpartyType, subCounterpartyType);
            }
            const multiParentsToUpdate = multiParentCounterparties.filter(parentCounterparty => !savedParentCounterparties.includes(parentCounterparty));
            for (const parentCounterpartyType of multiParentsToUpdate) {
                const strippedSubCounterpartyType: SubCounterpartyType = flow(
                    unset('subCounterpartyTypeId'),
                    set('parentCounterpartyType', parentCounterpartyType)
                )(subCounterpartyType);
                yield call(upsertOpinionSubCounterpartyType, strippedSubCounterpartyType);
            }
        }
        const scopes: OpinionScope[] = yield select(getOpinionScopesToUpdate);
        yield call(updateRelevantOpinionSummaries, { isSystem, jurisdiction, scopes });
        yield put(fetchOpinionSubCounterpartyTypesStarted(jurisdiction));
        yield put(upsertOpinionSubCounterpartyTypesSuccessful());
        toast(`Successfully ${subCounterpartyType.subCounterpartyTypeId ? 'updated' : 'added'} Sub Counterparty Type`);
    } catch (e) {
        yield put(upsertOpinionSubCounterpartyTypesFailed((e as Error).message));
    }
}

function* attemptUpsertOpinionSubCounterpartyTypesWatcher() {
    yield takeLeading(SubCounterpartyActionTypes.UPSERT_OPINION_SUB_COUNTERPARTY_TYPES_STARTED, attemptUpsertOpinionSubCounterpartyTypes);
}

export function* attemptDeleteOpinionSubCounterpartyTypes() {
    try {
        const subCounterpartiesToDelete: SubCounterpartyType[] | null = yield select(getSubCounterpartiesToDelete);
        if (!isNull(subCounterpartiesToDelete) && subCounterpartiesToDelete.length > 0) {
            const subCounterpartyType: SubCounterpartyType = yield select(getSelectedSubCounterpartyType);
            const { isSystem, jurisdiction } = subCounterpartyType;
            const subCounterpartyTypeIds = subCounterpartiesToDelete.map(({ subCounterpartyTypeId }) => subCounterpartyTypeId!);
            yield call(deleteSubCounterpartyTypes, { subCounterpartyTypeIds, jurisdiction, isSystem });
            yield put(fetchOpinionSubCounterpartyTypesStarted(jurisdiction));
            yield put(deleteOpinionSubCounterpartyTypesSuccessful());
            toast('Successfully deleted Sub Counterparty Type');
        }
    } catch (e) {
        yield put(deleteOpinionSubCounterpartyTypesFailed((e as Error).message));
    }
}

function* deleteOpinionSubCounterpartyTypesWatcher() {
    yield takeLeading(SubCounterpartyActionTypes.DELETE_OPINION_SUB_COUNTERPARTY_TYPES_STARTED, attemptDeleteOpinionSubCounterpartyTypes);
}

export function* attemptUpdateSelectedCounterpartySavedParents({ payload }: ReturnType<typeof updateOpinionSubCounterparty>) {
    const { key, value } = payload;
    if (key === 'subCounterpartyName') {
        const subCounterpartyType: SubCounterpartyType = yield select(getSelectedSubCounterpartyType);
        if (!isUndefined(subCounterpartyType.subCounterpartyTypeId)) {
            const allSubCounterpartyTypes: SubCounterpartyType[] = yield select(getAvailableSubCounterpartyTypes);
            const savedParentCounterparties: string[] = uniq([subCounterpartyType.parentCounterpartyType, ...allSubCounterpartyTypes.filter(({ subCounterpartyName }) => subCounterpartyName === value).map(({ parentCounterpartyType }) => parentCounterpartyType)]);
            yield put(setSavedParentCounterparties(savedParentCounterparties));
        }
    }
}

function* updateSelectedCounterpartyNameWatcher() {
    yield debounce(200, SubCounterpartyActionTypes.UPDATE_OPINION_SUB_COUNTERPARTY_VALUE, attemptUpdateSelectedCounterpartySavedParents);
}

export function* attemptUpdateSelectedCounterpartyMultiParents({ payload }: ReturnType<typeof setSelectedOpinionSubCounterparty>) {
    if (!isNull(payload)) {
        const allSubCounterpartyTypes: SubCounterpartyType[] = yield select(getAvailableSubCounterpartyTypes);
        const savedParentCounterparties: string[] = allSubCounterpartyTypes.filter(({ subCounterpartyName }) => subCounterpartyName === payload.subCounterpartyName).map(({ parentCounterpartyType }) => parentCounterpartyType);
        yield put(setSavedParentCounterparties(savedParentCounterparties));
    }
}

function* updateSelectedCounterpartyWatcher() {
    yield takeEvery(SubCounterpartyActionTypes.SET_SELECTED_OPINION_SUB_COUNTERPARTY, attemptUpdateSelectedCounterpartyMultiParents);
}

export function* subCounterpartySaga() {
    yield all([
        fork(attemptFetchAvailableOpinionSubCounterpartyTypesWatcher),
        fork(attemptUpsertOpinionSubCounterpartyTypesWatcher),
        fork(updateSelectedCounterpartyNameWatcher),
        fork(updateSelectedCounterpartyWatcher),
        fork(deleteOpinionSubCounterpartyTypesWatcher)
    ]);
}
